import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { ForecastListItem } from '@futrli-sdk/predict';
import {
  getConsolidatedForecastSalesUrl,
  getPredictForecastInvoicesUrl,
  getPredictForecastSalesUrl,
  PREDICT_PRODUCT_SLUG
} from '@futrli/shared-services';
import { LoadingWizardScreen } from '@futrli/ui-components/components/sidebar/wizard/screens/loading-wizard-screen/loading-wizard-screen';
import { ModifyConsolidatedForecastView } from '@futrli/views/views/forecasts/ModifyConsolidatedForecastView/ModifyConsolidatedForecastView';
import { ModifyForecastView } from '@futrli/views/views/forecasts/ModifyForecastView/ModifyForecastView';
import { useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard/shared/useDialogWizardState';

import { DuplicateForecastFormWizardScreen } from './screens/duplicate-forecast-form-wizard-screen';
import { ForecastDuplicatedWizardScreen } from './screens/forecast-duplicated-wizard-screen';
import { ForecastDuplicatingWizardScreen } from './screens/forecast-duplicating-wizard-screen';
import { trackDuplicateForecastEvent } from '../../../utils/utils';
import { useDuplicateForecastContext } from '../context/duplicate-forecast-context';

interface DuplicateForecastContainerProps {
  onClose: () => void;
}

export const DuplicateForecastContainer = ({ onClose }: DuplicateForecastContainerProps) => {
  const navigate = useNavigate();
  const { form, isConsolidation, organisationId, view, workspaceId } = useDuplicateForecastContext();
  const { forecast, isLoading, isDuplicating, errorDuplicating, newForecastId } = view.state;

  const handleSubmit = useCallback(() => {
    if (isConsolidation) {
      (view as ModifyConsolidatedForecastView).duplicateForecast(form.getConsolidatedRequestData()!);
    } else {
      (view as ModifyForecastView).duplicateForecast(form.getRequestData()!);
    }
  }, [form, isConsolidation, view]);

  const validateSubmit = useCallback(() => !!forecast && form.validate(), [forecast, form]);

  const handleView = useCallback(() => {
    if (forecast && newForecastId) {
      if (isConsolidation) {
        navigate(getConsolidatedForecastSalesUrl(workspaceId, organisationId, PREDICT_PRODUCT_SLUG, newForecastId));
      } else {
        const forecastItem = forecast as ForecastListItem;
        trackDuplicateForecastEvent(forecastItem.kind, forecastItem.include_unpaid_invoices, false);
        if (forecastItem.kind === 'scenario' && forecastItem.include_unpaid_invoices) {
          navigate(getPredictForecastInvoicesUrl(workspaceId, organisationId, newForecastId));
        } else {
          navigate(getPredictForecastSalesUrl(workspaceId, organisationId, newForecastId));
        }
      }
      onClose();
    }
  }, [forecast, isConsolidation, navigate, newForecastId, onClose, organisationId, workspaceId]);

  const { screen, handleAction } = useDialogWizardState({
    isLoading: isLoading || isDuplicating,
    error: errorDuplicating,
    onAction: handleSubmit,
    onClose,
    validate: validateSubmit
  });

  switch (screen) {
    case 'loading': {
      return isLoading ? <LoadingWizardScreen /> : <ForecastDuplicatingWizardScreen />;
    }
    case 'success': {
      return <ForecastDuplicatedWizardScreen onClose={onClose} onView={handleView} />;
    }
    default: {
      return (
        <DuplicateForecastFormWizardScreen
          onClose={onClose}
          onDuplicate={handleAction}
          error={errorDuplicating?.message}
        />
      );
    }
  }
};
